<template>
  <div
    v-loading="!isLoaded"
    element-loading-fullscreen="false"
    class="coverage-container"
  >
    <tf-table
      v-test:table="'coverage'"
      :class="{ 'with-feature-flag': ffUpcomingCoverage }"
      v-bind="{
        parentalControls: true,
        findIndexBy: 'product_id',
        tableData: coverages,
        tableMeta
      }"
      @changeSortCriteria="$emit('changeSortCriteria', $event)"
    >
      {{ /* rff:upcomingCoverageIsEnabled  */ }}
      <template v-if="ffUpcomingCoverage">
        <template slot="header">
          <tf-tabs v-model="activeCoverageType">
            <tf-tab-pane
              v-for="type in coverageTypes"
              :key="type"
              :disabled="type === 'upcoming' && !hasUpcomingCoverageDetails"
              :name="type"
              :label="`${$options.filters.capitalize(type)} coverage`"
            />
          </tf-tabs>
          <tf-button
            :disabled="!isCurrentCoverage"
            type="primary"
            icon="plus"
            size="text-icon"
            @click="onAddCurrentCoverage"
          >
            Add current coverage
          </tf-button>
        </template>
      </template>
      <template v-else>
        <template slot="title">
          Current coverage
        </template>
        <tf-button
          slot="ctaHeader"
          data-test-btn-add
          type="primary"
          icon="plus"
          size="text-icon"
          @click="onAddCurrentCoverage"
        >
          Add current coverage
        </tf-button>
      </template>
      <coverage-row
        slot="parentRow"
        v-bind="{
          coverageType: activeCoverageType,
          rowData
        }"
        slot-scope="{ rowData }"
        @updateCoverageProp="$store.commit(`${namespace}/setCoverageProp`, {
          productId: rowData.product_id,
          prop: $event.prop,
          value: $event.value,
        })"
      />
      <cta-download-coverage
        slot="footer"
        v-bind="{
          coverageType: activeCoverageType,
          clientId,
          isDisabled: !downloadIsEnabled,
          productTypeIds: coverages.map(({ product_type_id: productTypeId }) => productTypeId),
        }"
      >
        Download {{ activeCoverageType }} coverage
      </cta-download-coverage>
      <span
        v-if="error"
        slot="error"
        v-test:coverage-table-error
        v-text="error"
      />
      <empty-table-cell slot="empty">
        Current coverage hasn’t been added for {{ clientName }} yet.
        <tf-button
          slot="cta"
          v-test:button="'add current coverage'"
          type="secondary"
          icon="plus"
          size="medium"
          @click="onAddCurrentCoverage"
        >
          Add current coverage
        </tf-button>
      </empty-table-cell>
    </tf-table>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { upcomingCoverage } from '@/utils/featureFlags';

  /**
   * "TableCoverage" is a tabular table component that relies on a `client/${coverageType}Coverages` namespaced module, that has previoulsy been registered.
   * To add a new tab, the coverage "type" should be added to "this.coverageTypes".
   *
   * @vuedoc
   * @exports Client/Tables/Coverage
   * @category Components
   */
  export default {
    name: 'TableCoverage',
    components: {
      CtaDownloadCoverage: () => import('@/components/Ctas/DownloadCoverage'),
      CoverageRow: () => import('@/components/Employer/Tables/CoverageRow'),
      EmptyTableCell: () => import('@/components/EmptyTableCell'),
    },
    props: {
      /**
       * The ID of the employer used to interact with the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
      /**
       * The name of the employer to be used in the template.
       */
      clientName: {
        type: String,
        required: true,
      },
      /**
       * The sort direction from the parent to be used in the child table component.
       */
      sortDirection: {
        type: String,
        default: 'asc',
      },
      /**
       * The sort property from the parent to be used in the child table component.
       */
      sortProp: {
        type: String,
        default: 'product_type_name',
      },
    },
    data: () => ({
      activeCoverageType: 'current',
      coverageTypes: ['current', 'upcoming'],
      isDownloading: false,
    }),
    computed: {
      ...mapState({
        hasUpcomingCoverageDetails: (state) => state.employer.employerInfo.upcoming_coverage_detail_available,
      }),
      /**
       * Cache the namespaced module's coverages.
       *
       * @returns {Array}
       */
      coverages() {
        return this.$store.state[this.namespace].data;
      },
      /**
       * We only want to enabled the download current coverages footer cta if there is at least one completed coverage
       *
       * @returns {boolean}
       */
      downloadIsEnabled() {
        return this.coverages.some(({ state }) => state === 'completed');
      },
      /**
       * Cache the namespaced store's error to use in the template.
       *
       * @returns {string}
       */
      error() {
        return this.$store.state[this.namespace].error;
      },
      /**
       * Evaluate the "upcomingCoverage" feature flag to determine what to show in the template.
       * rff:upcomingCoverageIsEnabled
       *
       * @returns {boolean}
       */
      ffUpcomingCoverage() {
        return this.$ld.checkFlags(upcomingCoverage);
      },
      /**
       * Test the active coverage type for "current" to be use throughout the component.
       *
       * @returns {boolean}
       */
      isCurrentCoverage() {
        return this.activeCoverageType === 'current';
      },
      /**
       * Evaluate the 2 loaded states along with the active coverage type for a general loading state.
       *
       * @returns {boolean}
       */
      isLoaded() {
        return this.$store.state[this.namespace].isLoaded;
      },
      /**
       * Cache the name of the appropriate namespaced module.
       *
       * @returns {string}
       */
      namespace() {
        return `client/${this.activeCoverageType}Coverages`;
      },
      /**
       * Return an object to pass to the 'tableMeta' tf-table. The sort parameters are computed based on the instance props.
       *
       * @returns {object}
       */
      tableMeta() {
        const columns = this.isCurrentCoverage
          ? [
            {
              label: 'Renewal date',
              width: this.isCurrentCoverage ? '10%' : null,
              prop: 'renewal_date',
              sortable: true,
            },
            {
              label: null,
              prop: 'collect-cta',
              width: this.isCurrentCoverage ? '10%' : null,
            },
            {
              label: null,
              format: 'cta',
              prop: 'add-cta',
              width: this.isCurrentCoverage ? '10%' : null,
            },
          ]
          : [{
            label: 'Effective date',
            prop: 'effective_date',
            sortable: true,
          }];

        return {
          columns: [
            {
              label: 'Product(s)',
              prop: 'product_type_name',
              width: this.isCurrentCoverage ? '16%' : null,
              sortable: true,
            },
            {
              label: 'Carrier',
              prop: 'carrier_name',
              width: this.isCurrentCoverage ? '10%' : null,
              sortable: true,
            },
            {
              label: 'Commission',
              prop: 'commissions',
              width: this.isCurrentCoverage ? '14%' : null,
              sortable: true,
            },
            ...columns,
          ],
          sort: {
            direction: this.sortDirection,
            prop: this.sortProp,
          },
        };
      },
    },
    watch: {
      activeCoverageType: {
        /**
         * Load the coverages for the active coverage type.
         */
        handler() {
          this.loadCoverages();
        },
        immediate: true,
      },
    },
    methods: {
      /**
       * Load the upcoming coverages and set up the data, loading, and error states appropriately.
       */
      async loadCoverages() {
        if (this.$store.state[this.namespace].isLoaded) {
          return;
        }

        this.$store.dispatch(`${this.namespace}/loadCoverages`, {
          clientId: this.clientId,
          coverageType: this.activeCoverageType,
        });
      },
      /**
       * Start Add Current Coverage modal flow and track in analytics.
       */
      onAddCurrentCoverage() {
        this.$emit('clickAddCurrentCoverage');

        this.$TF.Analytics.trackEvent(
          'Add current coverage',
          { source: 'Current Coverage Table' },
        );
      },
      /**
       * Show an success or error message with the message using the passed 'action' param.
       *
       * @param {string} action
       * @param {boolean} success
       */
      showMessage(action, success = false) {
        this.$message({
          duration: 3000,
          message: `${action} ${success
            ? 'was successful.'
            : 'failed.'}`,
          showClose: true,
          type: success ? 'success' : 'error',
        });
      },
    },
  };
</script>

<style scoped lang="scss">
  .coverage-container {
    position: relative;
  }

  footer {
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    padding: 14px;
    width: 100%;
    border: 1px solid $tf-light-gray;
    border-top: none;
  }

  .with-feature-flag {
    ::v-deep {
      .el-tabs__nav-wrap::after {
        display: none;
      }

      .el-tabs__header {
        position: absolute;
        top: 23px;
      }

      .tf-table-header {
        padding: {
          top: 0;
          bottom: 0;
        }
      }
    }
  }
</style>
